import { DeleteAccountPanel } from '@/features/users/components/DeleteAccountPanel';
import { PasswordChangeForm } from '@/features/users/components/PasswordChangeForm';
import { UserProfileForm } from '@/features/users/components/UserProfileForm';
import { Panel, Section, Stack, Text } from '@/ui';
import { EmptyState } from '@/ui/composites/EmptyState';
import { PageShell } from '@/ui/primitives/PageShell';
import type { AuthSession } from '@/auth/types';
import type { DeleteAccountPanelState } from '@/features/users/hooks/useDeleteAccountPanel';
import type { PasswordChangeFormState } from '@/features/users/hooks/usePasswordChangeForm';
import type { UserProfileFormState } from '@/features/users/hooks/useUserProfileForm';
import type { ReactElement } from 'react';

type UserSettingsScreenProps = {
  profile: UserProfileFormState;
  userDetailError: string | null;
  password: PasswordChangeFormState;
  deleteAccount: DeleteAccountPanelState;
  fieldDirection: 'row' | 'column';
  session: AuthSession | null;
};

export const UserSettingsScreen = ({
  profile,
  userDetailError,
  password,
  deleteAccount,
  fieldDirection,
  session,
}: UserSettingsScreenProps): ReactElement => (
  <PageShell maxWidth="sm" paddingX="lg" paddingY="xl" paddingBottom="xl">
    <Stack gap="xl">
      <Section>
        <Stack gap="lg">
          <Text role="pageTitle" component="h1">
            Account Settings
          </Text>
          {userDetailError ? (
            <EmptyState title="Unable to load user settings" body={userDetailError} />
          ) : null}
          <Stack gap="lg">
            <Panel title="Account Details">
              <Stack gap="md">
                <Stack gap="xxs">
                  <Text role="body" color="textSecondary">
                    User ID
                  </Text>
                  <Text role="body">{session?.userId ?? ''}</Text>
                </Stack>
              </Stack>
            </Panel>
            <UserProfileForm fieldDirection={fieldDirection} profile={profile} />
            <PasswordChangeForm password={password} />
            <DeleteAccountPanel deleteAccount={deleteAccount} />
          </Stack>
        </Stack>
      </Section>
    </Stack>
  </PageShell>
);
